import { mapStringToTitle, Title } from '../components/wrestler/Title'
import { create } from 'zustand'
import { wordpressClient } from '../config/axiosConfig'

enum RosterType {
  NO_ROSTER_TYPE,
  WRESTLER,
  OFFICIAL,
}

function mapStringToRosterType(type: string): RosterType {
  switch (type) {
    case 'Wrestler':
      return RosterType.WRESTLER
    case 'Offizieller':
      return RosterType.OFFICIAL
    default:
      return RosterType.NO_ROSTER_TYPE
  }
}

export interface WrestlerData {
  id: number
  slug: string
  name: string
  herkunft: string
  gewicht: string
  groesse: string
  zugehoerigkeit: string[]
  titel: Title[]
  medaillen: number
  biografie: string
  profilbild: string
  finisher: string[]
  signature: string[]
  type: RosterType
}

interface WrestlerDataResponse {
  id: number
  slug: string
  acf: {
    name: string
    herkunft: string
    gewicht: string
    groesse: string
    zugehoerigkeit: string[]
    titel: string[]
    medaillen: number
    biografie: string
    profilbild: string
    finisher: string
    signature: string
    roster_type: string
  }
}

interface RosterState {
  wrestlers: WrestlerData[]
  fightbackChampion: WrestlerData | undefined
  collectorChampion: WrestlerData | undefined
  tagTeamChampions: WrestlerData[]
  collectors: WrestlerData[]
  officials: WrestlerData[]
  loaded: boolean
  getWrestler: (slug: string) => WrestlerData | undefined
}

const compareWrestlersByName = (wrestlerA: WrestlerData, wrestlerB: WrestlerData): number => {
  const nameA = wrestlerA.name.replace('"', '')
  const nameB = wrestlerB.name.replace('"', '')
  return nameA > nameB ? 1 : nameA < nameB ? -1 : 0
}

export const useRosterStore = create<RosterState>()(() => ({
  wrestlers: [],
  fightbackChampion: undefined,
  collectorChampion: undefined,
  tagTeamChampions: [],
  collectors: [],
  officials: [],
  loaded: false,
  getWrestler: (slug): WrestlerData | undefined => {
    const roster: WrestlerData[] = useRosterStore
      .getState()
      .wrestlers.concat(useRosterStore.getState().officials)
    let foundWrestler
    roster.forEach((wrestler) => {
      if (wrestler.slug === slug) {
        foundWrestler = wrestler
      }
    })
    return foundWrestler
  },
}))

wordpressClient
  .get<WrestlerDataResponse[]>('wrestler', {
    params: {
      // prettier-ignore
      'per_page': 100,
    },
  })
  .then((response) => {
    if (response.status === 200) {
      const roster: WrestlerData[] = response.data
        .map((wrestler) => ({
          id: wrestler.id,
          slug: wrestler.slug,
          name: wrestler.acf.name,
          herkunft: wrestler.acf.herkunft,
          gewicht: wrestler.acf.gewicht,
          groesse: wrestler.acf.groesse,
          zugehoerigkeit: wrestler.acf.zugehoerigkeit,
          titel: wrestler.acf.titel.map((titel): Title => {
            return mapStringToTitle(titel)
          }),
          medaillen: wrestler.acf.medaillen,
          biografie: wrestler.acf.biografie,
          profilbild: wrestler.acf.profilbild,
          finisher: wrestler.acf.finisher.replaceAll(', ', ',').split(','),
          signature: wrestler.acf.signature.replaceAll(', ', ',').split(','),
          type: mapStringToRosterType(wrestler.acf.roster_type),
        }))
        .sort(compareWrestlersByName)

      useRosterStore.setState({
        wrestlers: roster.filter((member) => member.type === RosterType.WRESTLER),
        fightbackChampion: roster.find((wrestler) =>
          wrestler.titel.includes(Title.FIGHTBACK_CHAMPION),
        ),
        collectorChampion: roster.find((wrestler) =>
          wrestler.titel.includes(Title.COLLECTORS_CHAMPION),
        ),
        tagTeamChampions: roster.filter((wrestler) =>
          wrestler.titel.includes(Title.TAG_TEAM_CHAMPION),
        ),
        collectors: roster.flatMap((wrestler) => Array(wrestler.medaillen).fill(wrestler)),
        officials: roster.filter((member) => member.type === RosterType.OFFICIAL),
        loaded: true,
      })
    } else {
      console.error('Error fetching roster data: ' + response)
    }
  })
  .catch((error) => {
    console.error('Error:', error)
  })
